import { CELL, GROUND } from '../core/config';
import { key, MATERIALS, riverX, type Building, type City, type Cell } from './city';
import type { Vec } from '../destruction/structure';

export interface Pasture {
  id: string;
  x: number;
  z: number;
  half: number;
  fence: number;
  water: number;
  drinking: Vec[];
  grazings: Vec[];
}
export interface Habitats {
  pastures: Pasture[];
}

/** Use genuinely vacant northern farm plots; keep the current map and herd size. */
export function prepareHabitats(city: City): City {
  if (city.habitats || !city.regional) return city;
  city.props ??= [];
  const pastures: Pasture[] = [];
  city.habitats = { pastures };
  let serial =
    Math.max(
      ...[...city.buildings, ...city.props, ...(city.infrastructure ?? []), ...(city.bridges ?? [])].map(
        (b) => b.id,
      ),
    ) + 1;
  const half = 12.5;
  const clear = (x: number, z: number) => {
    if (Math.abs(x - riverX(z)) < 45 || pastures.some((p) => Math.hypot(p.x - x, p.z - z) < 78)) return false;
    if (
      [...city.buildings, ...city.props!].some(
        (b) =>
          x + half + 2 > b.x &&
          x - half - 2 < b.x + b.w * CELL &&
          z + half + 2 > b.z &&
          z - half - 2 < b.z + b.d * CELL,
      )
    )
      return false;
    return !city.roadGraph!.edges.some((e) => {
      const a = city.roadGraph!.nodes[e.from],
        b = city.roadGraph!.nodes[e.to],
        m = e.width / 2 + 3;
      return (
        x + half > Math.min(a.x, b.x) - m &&
        x - half < Math.max(a.x, b.x) + m &&
        z + half > Math.min(a.z, b.z) - m &&
        z - half < Math.max(a.z, b.z) + m
      );
    });
  };
  for (let z = -580; z <= -445 && pastures.length < 4; z += 10)
    for (let x = -570; x <= -30 && pastures.length < 4; x += 10) {
      if (!clear(x, z)) continue;
      const b: Building = {
        id: serial++,
        name: `North Pastures · paddock ${pastures.length + 1}`,
        kind: 'fence',
        category: 'prop',
        district: 'farmland',
        x: x - half,
        z: z - half,
        baseY: GROUND,
        w: 21,
        d: 21,
        h: 1,
        cells: [],
        color: 0xb5ab8f,
        accent: 0x789277,
      };
      const put = (xx: number, zz: number, horizontal: boolean) => {
        const c: Cell = {
          id: b.id * 1e6 + key(xx, 0, zz),
          x: xx,
          y: 0,
          z: zz,
          root: true,
          material: 'wood',
          role: 'prop',
          hp: MATERIALS.wood.strength,
          color: b.color,
          bounds: horizontal
            ? { x: 0, y: 0, z: 0, w: 1, h: 0.9, d: 0.13 }
            : { x: 0, y: 0, z: 0, w: 0.13, h: 0.9, d: 1 },
        };
        // Two thin rails and their post share one stable, damageable identity.
        c.parts = horizontal
          ? [
              { x: 0, y: 0, z: 0, w: 0.13, h: 0.9, d: 0.13 },
              { x: 0, y: 0.35, z: 0, w: 1, h: 0.1, d: 0.13 },
              { x: 0, y: 0.72, z: 0, w: 1, h: 0.1, d: 0.13 },
            ]
          : [
              { x: 0, y: 0, z: 0, w: 0.13, h: 0.9, d: 0.13 },
              { x: 0, y: 0.35, z: 0, w: 0.13, h: 0.1, d: 1 },
              { x: 0, y: 0.72, z: 0, w: 0.13, h: 0.1, d: 1 },
            ];
        b.cells.push(c);
      };
      for (let k = 0; k < 20; k++) {
        put(k, 0, true);
        put(k, 20, true);
      }
      for (let k = 1; k < 20; k++) {
        put(0, k, false);
        put(20, k, false);
      }
      // Close the last southern corner with its own exact occupied cell.
      put(20, 20, false);
      city.props.push(b);
      const trough: Building = {
        id: serial++,
        name: `${b.name} · drinking trough`,
        kind: 'furniture',
        category: 'prop',
        district: 'farmland',
        x: x - 2.5,
        z: z - half + 2.5,
        baseY: GROUND,
        w: 4,
        d: 1,
        h: 1,
        color: 0x8e9f99,
        accent: 0x71a3ab,
        cells: [],
      };
      for (let k = 0; k < 4; k++)
        trough.cells.push({
          id: trough.id * 1e6 + key(k, 0, 0),
          x: k,
          y: 0,
          z: 0,
          root: true,
          role: 'prop',
          material: 'metal',
          hp: MATERIALS.metal.strength,
          color: trough.color,
          bounds: { x: 0, y: 0, z: 0, w: 1, h: 0.38, d: 0.7 },
          parts: [
            { x: 0, y: 0, z: 0, w: 1, h: 0.1, d: 0.7 },
            { x: 0, y: 0, z: 0, w: 1, h: 0.38, d: 0.08 },
            { x: 0, y: 0, z: 0.62, w: 1, h: 0.38, d: 0.08 },
            { x: 0, y: 0.23, z: 0.08, w: 1, h: 0.015, d: 0.54, color: trough.accent },
            ...(k === 0 || k === 3 ? [{ x: k ? 0.92 : 0, y: 0, z: 0, w: 0.08, h: 0.38, d: 0.7 }] : []),
          ],
        });
      city.props.push(trough);
      pastures.push({
        id: `pasture:${pastures.length}`,
        x,
        z,
        half,
        fence: b.id,
        water: trough.id,
        drinking: Array.from({ length: 4 }, (_, i) => ({
          x: trough.x + (i + 0.5) * CELL,
          y: GROUND,
          z: trough.z + 2.15,
        })),
        grazings: Array.from({ length: 25 }, (_, i) => ({
          x: x - 8 + (i % 5) * 4,
          y: GROUND,
          z: z - 5 + Math.floor(i / 5) * 3.1,
        })),
      });
    }
  return city;
}
